import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, ZoomIn, ZoomOut, Move, ExternalLink, MessageCircle, Tag, Calendar, Globe, Megaphone, Layout, Video, Mail, Trash2 } from "lucide-react";
import { SiFacebook, SiInstagram, SiTiktok, SiYoutube } from "react-icons/si";
import { FaLinkedin } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import AnnotationCanvas from "./AnnotationCanvas";
import AnnotationToolbar from "./AnnotationToolbar";
import CommentsPanel from "./CommentsPanel";

const typeIcons = { ad: Megaphone, landing_page: Layout, ugc: Video, email: Mail };
const typeLabels = { ad: "Ad", landing_page: "Landing Page", ugc: "UGC", email: "Email" };
const platformIcons = {
  Facebook: SiFacebook, Instagram: SiInstagram, TikTok: SiTiktok,
  LinkedIn: FaLinkedin, YouTube: SiYoutube, Email: Mail, Web: Globe, Other: Globe,
};

const ViewModal = ({ item, onClose, categories, onDelete }) => {
  const { api, user } = useAuth();

  const [showComments, setShowComments] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Zoom/pan state
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  // Annotation state
  const [activeTool, setActiveTool] = useState("none");
  const [color, setColor] = useState("#ff4444");
  const [thickness, setThickness] = useState(3);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [visibleUsers, setVisibleUsers] = useState([]);
  const [annotations, setAnnotations] = useState([]);

  const TypeIcon = typeIcons[item.content_type] || Megaphone;
  const category = categories?.find(c => c.id === item.category_id);

  const getPlatforms = () => {
    if (item.platforms && Array.isArray(item.platforms)) return item.platforms;
    if (item.platform) return item.platform.split(", ").filter(Boolean);
    return [];
  };
  const platformsList = getPlatforms();

  // Load annotations
  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/content/${item.id}/annotations`);
        setAnnotations(res.data);
        // Default: show all users
        const uids = [...new Set(res.data.map(a => a.user_id))];
        setVisibleUsers(uids);
      } catch (e) {
        // silently fail if no annotations
      }
    };
    if (item?.id) load();
  }, [item.id, api]);

  // Save annotation callback
  const handleSaveAnnotation = useCallback(async (annData) => {
    try {
      const res = await api.post(`/content/${item.id}/annotations`, annData);
      const newAnn = res.data;
      setAnnotations(prev => [...prev, newAnn]);
      // Ensure the new user is visible
      setVisibleUsers(prev => prev.includes(newAnn.user_id) ? prev : [...prev, newAnn.user_id]);
    } catch (e) {
      console.error("Failed to save annotation", e);
    }
  }, [api, item.id]);

  // Delete last own annotation
  const handleDeleteLast = useCallback(async () => {
    const ownAnnotations = annotations.filter(a => a.user_id === user?.id);
    if (!ownAnnotations.length) return;
    const last = ownAnnotations[ownAnnotations.length - 1];
    try {
      await api.delete(`/annotations/${last.id}`);
      setAnnotations(prev => prev.filter(a => a.id !== last.id));
    } catch (e) {
      console.error("Failed to delete annotation", e);
    }
  }, [annotations, api, user]);

  // Zoom/pan handlers — disabled when annotation tool is active
  const handleMouseDown = (e) => {
    if (activeTool !== "none") return;
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && activeTool === "none") {
      setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.5, 4));
  const handleZoomOut = () => {
    setZoom(prev => {
      const next = Math.max(prev - 0.5, 1);
      if (next <= 1) setPosition({ x: 0, y: 0 });
      return next;
    });
  };
  const resetZoom = () => { setZoom(1); setPosition({ x: 0, y: 0 }); };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (activeTool !== "none") { setActiveTool("none"); return; }
        onClose();
      }
      if (activeTool !== "none") return;
      if (e.key === "+" || e.key === "=") handleZoomIn();
      if (e.key === "-") handleZoomOut();
      if (e.key === "0") resetZoom();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, zoom, activeTool]);

  const handleDelete = () => {
    onDelete?.();
    onClose();
  };

  return (
    <>
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" />

      {/* Main image area */}
      <div className="relative flex-1 flex items-center justify-center p-8" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all z-10 border border-white/10"
        >
          <X size={18} />
        </button>

        {/* Annotation mode indicator */}
        {activeTool !== "none" && (
          <div className="absolute top-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/20 border border-violet-500/40 text-violet-300 text-[12px] font-medium backdrop-blur">
            {activeTool === "draw" ? "✏️ Mode dessin — Esc pour quitter" : "T Mode texte — cliquer pour placer"}
          </div>
        )}

        {/* Zoom controls (hidden when annotating) */}
        {activeTool === "none" && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/50 backdrop-blur-xl rounded-xl p-2 border border-white/10 z-10">
            <button
              onClick={handleZoomOut}
              disabled={zoom <= 1}
              className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 disabled:opacity-30 transition-all"
            >
              <ZoomOut size={16} />
            </button>
            <span className="text-[12px] text-white/50 w-12 text-center">{Math.round(zoom * 100)}%</span>
            <button
              onClick={handleZoomIn}
              disabled={zoom >= 4}
              className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 disabled:opacity-30 transition-all"
            >
              <ZoomIn size={16} />
            </button>
            <div className="w-px h-5 bg-white/10 mx-1" />
            <button
              onClick={resetZoom}
              className="px-3 py-1.5 rounded-lg bg-white/5 text-[11px] text-white/50 hover:text-white hover:bg-white/10 transition-all"
            >
              Reset
            </button>
            {zoom > 1 && (
              <div className="flex items-center gap-1.5 text-[10px] text-cyan-400/70 ml-2">
                <Move size={12} />
                <span>Glisser pour naviguer</span>
              </div>
            )}
          </div>
        )}

        {/* Image container */}
        <div
          ref={containerRef}
          className="relative max-w-[70%] max-h-[85vh] overflow-hidden rounded-2xl bg-[#0a0a0c] border border-white/10 shadow-2xl"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ cursor: activeTool !== "none" ? "default" : zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default" }}
        >
          {item.image_url ? (
            <img
              src={item.image_url}
              alt={item.title}
              className="max-w-full max-h-[85vh] object-contain transition-transform duration-200"
              style={{
                transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                transformOrigin: "center",
              }}
              draggable={false}
            />
          ) : (
            <div className="w-[500px] h-[400px] flex items-center justify-center bg-gradient-to-br from-[#151515] to-[#0a0a0a]">
              <TypeIcon size={64} className="text-white/10" />
            </div>
          )}

          {/* Annotation SVG overlay */}
          <AnnotationCanvas
            contentId={item.id}
            activeTool={activeTool}
            color={color}
            thickness={thickness}
            annotations={annotations}
            showAnnotations={showAnnotations}
            visibleUsers={visibleUsers}
            currentUserId={user?.id}
            onSave={handleSaveAnnotation}
            containerRef={containerRef}
          />
        </div>

        {/* Annotation toolbar */}
        <AnnotationToolbar
          activeTool={activeTool}
          onToolChange={setActiveTool}
          color={color}
          onColorChange={setColor}
          thickness={thickness}
          onThicknessChange={setThickness}
          showAnnotations={showAnnotations}
          onToggleAnnotations={() => setShowAnnotations(v => !v)}
          annotations={annotations}
          currentUserId={user?.id}
          visibleUsers={visibleUsers}
          onVisibleUsersChange={setVisibleUsers}
          onDeleteLast={handleDeleteLast}
        />
      </div>

      {/* Side panel */}
      <div
        className="relative w-[380px] bg-[#0c0c0e]/95 backdrop-blur-xl border-l border-white/[0.08] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "slideInRight 0.3s ease-out" }}
      >
        {/* Header */}
        <div className="p-6 border-b border-white/[0.06]">
          {category && (
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }} />
              <span className="text-[11px] text-white/40">{category.name}</span>
            </div>
          )}
          <h2 className="text-lg font-semibold text-white leading-tight mb-3">{item.title}</h2>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/[0.05] border border-white/[0.08]">
              <TypeIcon size={12} className="text-violet-400" />
              <span className="text-[11px] text-white/60">{typeLabels[item.content_type]}</span>
            </div>
            {platformsList.map((p, i) => {
              const PIcon = platformIcons[p] || Globe;
              return (
                <div key={i} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/[0.05] border border-white/[0.08]">
                  <PIcon size={12} className="text-cyan-400" />
                  <span className="text-[11px] text-white/60">{p}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {item.description && (
            <div>
              <h3 className="text-[11px] text-white/30 uppercase tracking-wider font-medium mb-2">Description</h3>
              <p className="text-[13px] text-white/70 leading-relaxed">{item.description}</p>
            </div>
          )}

          {/* Annotations summary */}
          {annotations.length > 0 && (
            <div>
              <h3 className="text-[11px] text-white/30 uppercase tracking-wider font-medium mb-2">Annotations ({annotations.length})</h3>
              <div className="space-y-2">
                {[...new Set(annotations.map(a => a.user_id))].map(uid => {
                  const userAnns = annotations.filter(a => a.user_id === uid);
                  const uname = userAnns[0].username;
                  return (
                    <div key={uid} className="flex items-center gap-2 text-[12px] text-white/50">
                      <div className="w-5 h-5 rounded-full bg-violet-600 flex items-center justify-center text-[9px] font-bold text-white">
                        {uname[0].toUpperCase()}
                      </div>
                      <span>{uname}</span>
                      <span className="ml-auto text-white/30">{userAnns.length} annotation{userAnns.length > 1 ? "s" : ""}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {item.tags?.length > 0 && (
            <div>
              <h3 className="text-[11px] text-white/30 uppercase tracking-wider font-medium mb-2 flex items-center gap-1.5">
                <Tag size={11} />
                Tags
              </h3>
              <div className="flex items-center gap-1.5 flex-wrap">
                {item.tags.map((tag, i) => (
                  <span key={i} className="text-[11px] text-cyan-400/80 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-2.5 py-1">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {item.source_url && (
            <div>
              <h3 className="text-[11px] text-white/30 uppercase tracking-wider font-medium mb-2">Source</h3>
              <a
                href={item.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-[12px] text-violet-400 hover:text-violet-300 transition-colors group"
              >
                <ExternalLink size={13} className="group-hover:translate-x-0.5 transition-transform" />
                <span className="truncate">{item.source_url}</span>
              </a>
            </div>
          )}

          {item.created_at && (
            <div className="flex items-center gap-2 text-[11px] text-white/30">
              <Calendar size={12} />
              <span>Ajouté le {new Date(item.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
            </div>
          )}
        </div>

        {/* Actions footer */}
        <div className="p-4 border-t border-white/[0.06] space-y-2">
          <button
            onClick={() => setShowComments(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white text-black text-[13px] font-medium hover:bg-white/90 transition-all"
          >
            <MessageCircle size={15} />
            Voir les commentaires
          </button>
          {item.source_url && (
            <a
              href={item.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.06] border border-white/[0.1] text-white/70 text-[13px] font-medium hover:bg-white/[0.1] hover:text-white transition-all"
            >
              <ExternalLink size={15} />
              Ouvrir la source
            </a>
          )}
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-[13px] font-medium hover:bg-red-500/20 transition-all"
            >
              <Trash2 size={15} />
              Supprimer
            </button>
          ) : (
            <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-3">
              <p className="text-[12px] text-red-300 text-center mb-2.5">Supprimer définitivement ce contenu ?</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-2 rounded-lg bg-white/[0.05] border border-white/[0.1] text-white/60 text-[12px] hover:text-white transition-all"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 py-2 rounded-lg bg-red-500/80 text-white text-[12px] font-medium hover:bg-red-500 transition-all"
                >
                  Supprimer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
    {showComments && <CommentsPanel item={item} onClose={() => setShowComments(false)} />}
    </>
  );
};

export default ViewModal;
